import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { ArrowRight, Briefcase, Inbox, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../lib/admin-api';
import { formatDateTime, formatRelative } from './formatters';

function StatCard({ icon: Icon, label, total, newCount, sinceCount = 0, to, accent = 'primary', testId }) {
  return (
    <Card className="editorial-card p-0" data-testid={testId}>
      <div className="spec-strip" />
      <CardContent className="p-6 pt-7">
        <div className="flex items-start justify-between mb-4">
          <div className={`w-11 h-11 rounded-lg flex items-center justify-center border ${accent === 'primary' ? 'bg-primary/8 border-primary/15' : 'bg-[hsl(var(--steel-blue)/0.12)] border-[hsl(var(--steel-blue)/0.25)]'}`}>
            <Icon className={`w-5 h-5 ${accent === 'primary' ? 'text-primary' : 'text-[hsl(var(--steel-blue))]'}`} strokeWidth={1.6} />
          </div>
          {newCount > 0 ? (
            <Badge variant="secondary" className="font-mono text-[10px] tracking-[0.18em] uppercase bg-primary/15 text-primary border-primary/25">
              {newCount} New
            </Badge>
          ) : null}
        </div>
        <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-1.5">{label}</p>
        <p className="font-display text-3xl font-semibold tracking-tight text-foreground mb-1">{total}</p>
        {sinceCount > 0 ? (
          <p className="text-[11px] text-primary/90 mb-3" data-testid={`${testId}-since`}>
            +{sinceCount} since your last visit
          </p>
        ) : (
          <div className="mb-4" />
        )}
        <Link
          to={to}
          className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
          data-testid={`${testId}-view-all`}
        >
          View all
          <ArrowRight className="w-3 h-3" />
        </Link>
      </CardContent>
    </Card>
  );
}

function RecentList({ title, items, emptyText, renderItem, linkTo, testId }) {
  return (
    <Card className="editorial-card p-0" data-testid={testId}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground">{title}</p>
          <Link to={linkTo} className="text-xs text-primary hover:underline">View all</Link>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">{emptyText}</p>
        ) : (
          <ul className="divide-y divide-bone-2">
            {items.map((item, idx) => (
              <li key={item._id || idx} className="py-3 first:pt-0 last:pb-0">
                {renderItem(item)}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchOverview = useCallback(async ({ silent } = {}) => {
    if (silent) setRefreshing(true);
    try {
      const response = await adminApi.get('/overview');
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  return (
    <AdminLayout
      title="Dashboard"
      subtitle="At-a-glance view of new applications and contact submissions."
    >
      <div className="flex justify-end mb-5">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => fetchOverview({ silent: true })}
          disabled={refreshing}
          data-testid="admin-overview-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Skeleton className="h-44" />
          <Skeleton className="h-44" />
        </div>
      ) : (
        <>
          {(() => {
            const sinceApps = data?.applications?.since_last_visit || 0;
            const sinceContacts = data?.contacts?.since_last_visit || 0;
            if (sinceApps + sinceContacts === 0) return null;
            const parts = [];
            if (sinceApps > 0) parts.push(`${sinceApps} new ${sinceApps === 1 ? 'application' : 'applications'}`);
            if (sinceContacts > 0) parts.push(`${sinceContacts} new ${sinceContacts === 1 ? 'inquiry' : 'inquiries'}`);
            return (
              <div
                className="mb-5 flex items-center gap-3 rounded-lg border border-primary/20 bg-primary/[0.06] px-4 py-3"
                data-testid="new-since-visit-banner"
              >
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                </span>
                <p className="text-[13px] text-foreground/80">
                  <span className="font-semibold text-foreground">Welcome back.</span>{' '}
                  {parts.join(' and ')} since your last visit
                  {data?.last_visit_at ? ` on ${formatDateTime(data.last_visit_at)}` : ''}.
                </p>
              </div>
            );
          })()}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5">
            <StatCard
              icon={Briefcase}
              label="Job Applications"
              total={data?.applications?.total || 0}
              newCount={data?.applications?.new || 0}
              sinceCount={data?.applications?.since_last_visit || 0}
              to="/admin/applications"
              accent="primary"
              testId="stat-applications"
            />
            <StatCard
              icon={Inbox}
              label="Contact Inquiries"
              total={data?.contacts?.total || 0}
              newCount={data?.contacts?.new || 0}
              sinceCount={data?.contacts?.since_last_visit || 0}
              to="/admin/contacts"
              accent="steel"
              testId="stat-contacts"
            />
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
            <RecentList
              title="Recent Applications"
              items={data?.applications?.recent || []}
              emptyText="No applications yet."
              linkTo="/admin/applications"
              testId="recent-applications"
              renderItem={(item) => (
                <Link to={`/admin/applications/${item._id}`} className="block group">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-display font-semibold text-sm text-foreground group-hover:text-primary transition-colors truncate">
                        {item.full_name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{item.role_applying_for}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[11px] text-muted-foreground" title={formatDateTime(item.submitted_at)}>
                        {formatRelative(item.submitted_at)}
                      </p>
                      <Badge variant="secondary" className="font-mono text-[9px] tracking-[0.18em] uppercase mt-1">
                        {item.status || 'new'}
                      </Badge>
                    </div>
                  </div>
                </Link>
              )}
            />
            <RecentList
              title="Recent Contact Inquiries"
              items={data?.contacts?.recent || []}
              emptyText="No contact submissions yet."
              linkTo="/admin/contacts"
              testId="recent-contacts"
              renderItem={(item) => (
                <Link to={`/admin/contacts/${item._id}`} className="block group">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-display font-semibold text-sm text-foreground group-hover:text-primary transition-colors truncate">
                        {item.name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {item.company || item.project_type || 'Inquiry'}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[11px] text-muted-foreground" title={formatDateTime(item.submitted_at)}>
                        {formatRelative(item.submitted_at)}
                      </p>
                      <Badge variant="secondary" className="font-mono text-[9px] tracking-[0.18em] uppercase mt-1">
                        {item.status || 'new'}
                      </Badge>
                    </div>
                  </div>
                </Link>
              )}
            />
          </div>
        </>
      )}
    </AdminLayout>
  );
}
